import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'
import { ConfirmationDialog, FieldMessage, TextInput, useTranslate } from '@athena/ui'
import { updateProfileNamesInput } from '@athena/validation'
import { useEffect, useState } from 'react'
import { MeDocument, UpdateProfileNamesDocument } from '../../graphql/generated/operations.js'

export function ProfileEditor({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const t = useTranslate()
  const client = useApolloClient()
  const { data } = useQuery(MeDocument)
  const [save] = useMutation(UpdateProfileNamesDocument)
  const [firstName, setFirstName] = useState(data?.me?.onboarding.firstName ?? '')
  const [lastName, setLastName] = useState(data?.me?.onboarding.lastName ?? '')
  const [error, setError] = useState<string>()
  const [dirty, setDirty] = useState(false)
  useEffect(() => {
    if (dirty || !data?.me) return
    setFirstName(data.me.onboarding.firstName)
    setLastName(data.me.onboarding.lastName)
  }, [data, dirty])
  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={onOpenChange}
      intent="default"
      title={t('profile.title')}
      description={t('profile.description')}
      confirmLabel={t('common.save')}
      cancelLabel={t('common.cancel')}
      failure={error ? <FieldMessage tone="error">{error}</FieldMessage> : null}
      onConfirm={async () => {
        const parsed = updateProfileNamesInput.safeParse({ firstName, lastName })
        if (!parsed.success) {
          setError(t('onboarding.nameError'))
          return false
        }
        try {
          await save({ variables: { input: parsed.data } })
          // Every active list/report reading names needs fresh joined identity data.
          await client.refetchQueries({ include: 'active' })
          return true
        } catch {
          setError(t('onboarding.error'))
          return false
        }
      }}
    >
      <div className="flex flex-col gap-4">
        <TextInput
          label={t('onboarding.firstName')}
          value={firstName}
          onChange={(event) => {
            setDirty(true)
            setFirstName(event.target.value)
          }}
          autoComplete="given-name"
          maxLength={60}
          required
        />
        <TextInput
          label={t('onboarding.lastName')}
          value={lastName}
          onChange={(event) => {
            setDirty(true)
            setLastName(event.target.value)
          }}
          autoComplete="family-name"
          maxLength={60}
          required
        />
      </div>
    </ConfirmationDialog>
  )
}
